import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

// One row in the dropdown list. Mirrors the shape every per-tab response
// uses for its `tangrams[]` option list (Content Metrics / Educator View /
// Event Tracking) so the component can drop in without per-tab adapters.
export interface TangramSelectOption {
  pathwayId: string;
  title: string;
  mediaCount: number;
}

// Themed, searchable Tangram picker. Replaces the native `<select>` we
// were rendering across three tabs. Features:
//
//   • Typeahead filter (case-insensitive substring match over `title`).
//   • Keyboard navigation — ↑ / ↓ to move, Enter to select, Escape to close,
//     auto-scrolls the highlighted option into view.
//   • Theme-driven colours (`var(--tangram-primary)`, `var(--tangram-panel)`,
//     `var(--tangram-text)`) so it adapts to dark / light / professor modes.
//   • Single-source config — selected value is one-way bound (`[selected]`)
//     and changes leave through `(pathwayChange)`, so existing tabs that
//     manage `selectedPathwayId` locally only need to swap the markup.
//
// Sentinel: an empty `selected` (or the explicit `''` option) means the
// "All Tangrams" choice. Tabs already use that sentinel so no migration
// is needed at the call sites.
@Component({
  selector: 'tangram-analytics-tangram-select',
  templateUrl: './analytics-tangram-select.component.html',
  styleUrls: ['./analytics-tangram-select.component.scss'],
})
export class AnalyticsTangramSelectComponent implements OnChanges {
  // The full list of options the parent fetches from the analytics
  // service. Component takes a clone so it can sort / filter without
  // mutating the parent array.
  @Input() tangrams: TangramSelectOption[] = [];

  // Currently-selected pathwayId. Empty string = "All Tangrams".
  @Input() selected: string = '';

  // Override copy for the leading "All Tangrams" row + the disabled-state
  // placeholder so per-tab phrasing stays in the parent's hands.
  @Input() allLabel = 'All Tangrams';
  @Input() placeholder = 'Search Tangrams…';
  @Input() emptyLabel = 'No Tangrams match your search';
  @Input() disabled = false;

  @Output() pathwayChange = new EventEmitter<string>();

  isOpen = false;
  search = '';

  // 0-based active row inside `visibleOptions` for keyboard navigation.
  // -1 means "nothing highlighted yet" — rare; first arrow press jumps
  // to the first row. Reset every time the panel opens or search changes.
  highlightIndex = -1;

  @ViewChild('searchInput', { static: false }) searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('optionsList', { static: false }) optionsList?: ElementRef<HTMLUListElement>;

  constructor(private host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Re-clamp the highlight when the option list changes so a refresh
    // never points at a row that no longer exists.
    if (changes['tangrams']) {
      const visibleLength = this.visibleOptions.length;
      if (this.highlightIndex >= visibleLength) {
        this.highlightIndex = visibleLength - 1;
      }
    }
  }

  // Display string for the closed trigger button. Mirrors the chip the
  // tabs used to render in the filter summary so users see a familiar
  // value at all times.
  get triggerLabel(): string {
    if (!this.selected) {
      return `${this.allLabel} (${this.tangrams.length})`;
    }
    const match = this.tangrams.find((t) => t.pathwayId === this.selected);
    if (!match) {
      // Server may have echoed back an empty filter (e.g. unknown id).
      // Fall back to "All Tangrams" rather than an empty trigger.
      return `${this.allLabel} (${this.tangrams.length})`;
    }
    return `${match.title} (${match.mediaCount} media)`;
  }

  // Filtered list rendered inside the panel. The "All Tangrams" pseudo-row
  // is prepended unconditionally — it always matches because it has no
  // backing data — so users can clear the filter even after typing.
  get visibleOptions(): Array<TangramSelectOption | null> {
    const needle = this.search.trim().toLowerCase();
    const filtered = !needle
      ? this.tangrams.slice()
      : this.tangrams.filter((t) => (t.title || '').toLowerCase().indexOf(needle) !== -1);
    // `null` is the sentinel for the "All Tangrams" pseudo-row so the
    // template can keep one *ngFor over a heterogeneous list rather than
    // splitting it into "the all row + the rest".
    return [null as TangramSelectOption | null, ...filtered];
  }

  open(): void {
    if (this.disabled || this.isOpen) { return; }
    this.isOpen = true;
    this.search = '';
    this.highlightIndex = this.indexOfSelectedInVisible();
    // Focus the search input after Angular paints the panel — without
    // the timeout the input element doesn't exist yet.
    setTimeout(() => {
      this.searchInput && this.searchInput.nativeElement.focus();
      this.scrollHighlightIntoView();
    }, 0);
  }

  close(): void {
    this.isOpen = false;
    this.search = '';
  }

  toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  onSearchChange(value: string): void {
    this.search = value;
    // Reset to the first visible row so Enter + the visible cursor stay
    // in sync as the list shrinks.
    this.highlightIndex = this.visibleOptions.length > 0 ? 0 : -1;
    setTimeout(() => this.scrollHighlightIntoView(), 0);
  }

  selectOption(option: TangramSelectOption | null): void {
    const value = option ? option.pathwayId : '';
    this.close();
    if (value !== this.selected) {
      this.pathwayChange.emit(value);
    }
  }

  // ── Keyboard ─────────────────────────────────────────────────────
  onKeydown(event: KeyboardEvent): void {
    if (!this.isOpen) {
      // From the closed trigger: Enter / Space / ArrowDown opens.
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        this.open();
      }
      return;
    }

    const list = this.visibleOptions;
    if (!list.length) {
      if (event.key === 'Escape') { event.preventDefault(); this.close(); }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.highlightIndex = (this.highlightIndex + 1) % list.length;
      this.scrollHighlightIntoView();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.highlightIndex = (this.highlightIndex - 1 + list.length) % list.length;
      this.scrollHighlightIntoView();
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.highlightIndex = 0;
      this.scrollHighlightIntoView();
    } else if (event.key === 'End') {
      event.preventDefault();
      this.highlightIndex = list.length - 1;
      this.scrollHighlightIntoView();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const target = list[this.highlightIndex];
      // `target` may be the `null` sentinel (the "All Tangrams" row) — that's
      // fine, `selectOption(null)` clears the filter.
      this.selectOption(target);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  // Outside-click closes the panel. Bound at the document level so users
  // can dismiss without clicking another control first.
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) { return; }
    const target = event.target as Node | null;
    if (target && this.host.nativeElement.contains(target)) { return; }
    this.close();
  }

  // ── Helpers ──────────────────────────────────────────────────────
  isSelected(option: TangramSelectOption | null): boolean {
    return option ? option.pathwayId === this.selected : !this.selected;
  }

  trackByPathwayId(_idx: number, option: TangramSelectOption | null): string {
    return option ? option.pathwayId : '__all__';
  }

  private indexOfSelectedInVisible(): number {
    const list = this.visibleOptions;
    for (let i = 0; i < list.length; i += 1) {
      if (this.isSelected(list[i])) { return i; }
    }
    return 0;
  }

  // Keep the keyboard cursor visible when navigating a long list. Reads
  // the rendered DOM rather than maintaining a parallel index → element
  // map because the option list is short (typically <500 rows).
  private scrollHighlightIntoView(): void {
    if (!this.optionsList || this.highlightIndex < 0) { return; }
    const ul = this.optionsList.nativeElement;
    const target = ul.children[this.highlightIndex] as HTMLElement | undefined;
    if (target && typeof target.scrollIntoView === 'function') {
      // `block: 'nearest'` keeps scrolling minimal — no jarring jumps when
      // the cursor moves within the visible window.
      target.scrollIntoView({ block: 'nearest' });
    }
  }
}
